/**
 * Page-local URL params for the three pieces of TaxReturnsPage state that were NOT surviving
 * back-navigation or a refresh: `year`, `inactive` and the search box (`q`).
 *
 * These are deliberately NOT part of the shared `useListSearchParams` codec
 * (components/listFilters/useListSearchParams.ts). That file is a fleet standard carried
 * verbatim by ptc-core and ptc-supervision — its code is identical in all three repos — and
 * `year`/`inactive`/`q` are this page's own scope, not generic list-filter grammar. The
 * governing reason is the same distinction the fleet contract draws: `setFilters` replaces
 * every `filter.*` param wholesale, so a fetch scope folded in there sits in that blast radius
 * and an ordinary filter change could silently drop it.
 *
 * Conventions (mirroring the shared codec exactly):
 * - `year=<yyyy>` is OMITTED when it equals `defaultYear`; `year=all` represents "All Years"
 *   (selectedYear === undefined); any other integer is a literal tax year. A malformed value
 *   falls back to `defaultYear` — never throws, never leaks NaN into state.
 * - `inactive=1` is present ONLY when true, omitted when false. This cannot distinguish
 *   "explicitly false" from "unset" — accepted, because this is a stable binary default with no
 *   third state. Do NOT copy the pattern onto a tri-state field, where it would be wrong.
 * - `q=<text>` is present only when non-empty; an empty or whitespace-only box omits it.
 * - Changing any of them clears `page` (back to 1), matching the shared codec's page hygiene.
 * - Mutations replace history (never push), matching the shared codec.
 * - A pristine view (defaultYear, inactive=false, empty search) produces a URL with NONE of them.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface TaxReturnsScopeParams {
  /** Effective selected tax year; `undefined` means "All Years". */
  year: number | undefined;
  inactive: boolean;
  /** Effective search text; `''` when absent. */
  q: string;
  /** Set the selected year. `defaultYear` omits the param; `undefined` writes `year=all`. */
  setYear: (year: number | undefined) => void;
  /** Set the inactive-visibility toggle. `false` (the default) omits the param. */
  setInactive: (inactive: boolean) => void;
  /** Set the search text. Empty/whitespace-only omits the param. */
  setQ: (q: string) => void;
}

export function useTaxReturnsScopeParams(defaultYear: number): TaxReturnsScopeParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const year = useMemo<number | undefined>(() => {
    const raw = searchParams.get('year');
    if (raw === null) return defaultYear;
    if (raw === 'all') return undefined;
    const n = Number(raw);
    return Number.isInteger(n) ? n : defaultYear;
  }, [searchParams, defaultYear]);

  const inactive = searchParams.get('inactive') === '1';
  const q = searchParams.get('q') ?? '';

  /**
   * All mutations replace history (never push) and go through the functional form, matching
   * useListSearchParams — safe against a stale `searchParams` closure when both hooks (or two
   * calls of this one) update the same URL in quick succession.
   */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setYear = useCallback(
    (next: number | undefined) => {
      update((params) => {
        if (next === defaultYear) params.delete('year');
        else if (next === undefined) params.set('year', 'all');
        else params.set('year', String(next));
        params.delete('page');
      });
    },
    [update, defaultYear]
  );

  const setInactive = useCallback(
    (next: boolean) => {
      update((params) => {
        if (next) params.set('inactive', '1');
        else params.delete('inactive');
        params.delete('page');
      });
    },
    [update]
  );

  const setQ = useCallback(
    (next: string) => {
      update((params) => {
        if (next.trim() === '') params.delete('q');
        else params.set('q', next);
        params.delete('page');
      });
    },
    [update]
  );

  return { year, inactive, q, setYear, setInactive, setQ };
}
